import React, { useState } from 'react';
import { Send, User, Mail, Phone, Building2, MessageSquare, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const ContactForm = ({ variant = 'default', title, subtitle }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});

  const subjects = [
    'Orçamento de Empilhadeira',
    'Locação de Equipamentos',
    'Manutenção e Reforma',
    'Pneus e Acessórios',
    'Terceirização',
    'Outros'
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Telefone é obrigatório';
    if (!formData.message.trim()) newErrors.message = 'Mensagem é obrigatória';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setIsError(false);
    setErrorMessage('');
    
    try {
      // Envia para o PHP - detecta automaticamente o caminho base
      const response = await fetch('api/contato.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSuccess(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            subject: '',
            message: ''
          });
        }, 4000);
      } else {
        setIsError(true);
        setErrorMessage(result.message || 'Erro ao enviar mensagem. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setIsError(true);
      setErrorMessage('Erro de conexão. Por favor, tente novamente ou entre em contato por telefone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Limpar erro geral ao digitar
    if (isError) {
      setIsError(false);
      setErrorMessage('');
    }
  };

  const inputClasses = (field) => `
    w-full px-4 py-3 pl-12 rounded-xl border transition-all duration-300
    ${errors[field] 
      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
      : 'border-gray-200 bg-white focus:border-teal-500 focus:ring-teal-500/20'
    }
    focus:outline-none focus:ring-4
  `;

  if (isSuccess) {
    return (
      <div className={`${variant === 'compact' ? 'p-6' : 'p-8'} bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl border border-teal-100`}>
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle size={40} className="text-teal-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Mensagem Enviada!</h3>
          <p className="text-gray-600">Entraremos em contato em breve.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${variant === 'compact' ? 'p-6' : 'p-8'} bg-white rounded-2xl shadow-xl border border-gray-100`}>
      {(title || subtitle) && (
        <div className="mb-8">
          {title && <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>}
          {subtitle && <p className="text-gray-600">{subtitle}</p>}
        </div>
      )}

      {/* Mensagem de erro */}
      {isError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">Erro ao enviar</p>
            <p className="text-red-600 text-sm">{errorMessage}</p>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Name */}
          <div className="relative">
            <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Seu nome *"
              className={inputClasses('name')}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div className="relative">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Seu e-mail *"
              className={inputClasses('email')}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div className="relative">
            <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Seu telefone *"
              className={inputClasses('phone')}
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          {/* Company */}
          <div className="relative">
            <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Empresa (opcional)"
              className={inputClasses('company')}
            />
          </div>
        </div>

        {/* Subject */}
        <div className="relative">
          <MessageSquare size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full px-4 py-3 pl-12 rounded-xl border border-gray-200 bg-white focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 focus:outline-none transition-all duration-300 appearance-none cursor-pointer"
          >
            <option value="">Selecione o assunto</option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Sua mensagem *"
            rows={variant === 'compact' ? 3 : 5}
            className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 resize-none ${
              errors.message 
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20' 
                : 'border-gray-200 bg-white focus:border-teal-500 focus:ring-teal-500/20'
            } focus:outline-none focus:ring-4`}
          />
          {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white py-4 rounded-xl font-semibold shadow-lg shadow-teal-500/20 hover:shadow-teal-500/40 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send size={20} />
              Enviar Mensagem
            </>
          )}
        </button>

        {/* WhatsApp alternative */}
        <p className="text-center text-sm text-gray-500">
          Ou fale diretamente pelo{' '}
          <a 
            href={`https://wa.me/${companyInfo.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-600 font-medium hover:underline"
          >
            WhatsApp
          </a>
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
